/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, March 3rd 2022, 11:37:12 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { Typography } from "ui-widgets";
import Tooltip from "@mui/material/Tooltip";
import Paper from "@mui/material/Paper";
import ListItemButton, { ListItemButtonProps } from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

interface ToolBarItemButtonProps {
  tooltipText: React.ReactNode;
  buttonIcon: React.ReactNode;
  buttonTitle: React.ReactNode;
  buttonCursor?: React.CSSProperties["cursor"];
  buttonProps?: Partial<ListItemButtonProps>;
}

const ToolBarItemButtonLayout = ({
  tooltipText,
  buttonIcon,
  buttonTitle,
  disableRipple,
  buttonCursor,
  buttonProps,
}: ToolBarItemButtonProps & Pick<ListItemButtonProps, "disableRipple">) => {
  return (
    <Tooltip
      placement="right"
      arrow
      title={tooltipText ? <Typography variant="caption">{tooltipText}</Typography> : ""}
    >
      <ListItemButton sx={{ cursor: buttonCursor ?? "move" }} disableRipple={disableRipple} {...buttonProps}>
        <ListItemIcon sx={{ color: "inherit" }}>{buttonIcon}</ListItemIcon>
        <ListItemText primary={buttonTitle} primaryTypographyProps={{ fontSize: 14, fontWeight: "medium" }} />
      </ListItemButton>
    </Tooltip>
  );
};

export const ToolBarItemButton = (props: ToolBarItemButtonProps) => (
  <Paper elevation={6} className="os-left-toolbar-item">
    <ToolBarItemButtonLayout {...props} />
  </Paper>
);

export const ToolBarItemDragLayer = (props: Omit<ToolBarItemButtonProps, "tooltipText">) => (
  <Paper elevation={12} className="os-toolbar-item-drag-preview">
    {/* Disabling Material UI ripple effect here because it adversely affects react-dnd performance and the HTML5 default drag preview by showing the wrong preview. https://github.com/react-dnd/react-dnd/issues/832 */}
    {/* TODO: Consider not using Material UI components for custom drag previews and instead using pure CSS because they appear to have better performance */}
    <ToolBarItemButtonLayout {...props} tooltipText={null} disableRipple={true} />
  </Paper>
);
